import {
  array,
  boolean as booleanType,
  date,
  Describe,
  nullable,
  object,
  optional,
  string,
} from 'superstruct';

import {
  AugmentedCreateEntityInput,
  AugmentedCreateIrrevocableTrustInput,
  AugmentedUpdateEntityInput,
  AugmentedUpdateIrrevocableTrustInput,
} from '@/types/schema';

import {
  createInsurancePolicyInput,
  updateInsurancePolicyInput,
} from './insuranceTypes';
import {
  beneficiarySchema,
  commonUpdateEntityProperties,
  createAccountSchema,
  createEntityInput,
  grantorSchema,
  gstStatusSchema,
  inclusionRatioSchema,
  inEstateStatusSchema,
  taxStatusSchema,
  trustAdvisorSchema,
  trusteeSchema,
  updateAccountSchema,
} from './types';
import { $getValidatedInput } from './utils';

const createIrrevocableTrustSchema: Describe<AugmentedCreateIrrevocableTrustInput> =
  object({
    create: object({
      trustJurisdictionStateCode: optional(nullable(string())),
      taxStatus: taxStatusSchema,
      legalName: optional(nullable(string())),
      inEstateStatus: inEstateStatusSchema,
      stateInEstateStatus: inEstateStatusSchema,
      inclusionRatio: inclusionRatioSchema,
      gstStatus: gstStatusSchema,
      effectiveDate: optional(nullable(date())),
      displayName: string(),
      description: optional(nullable(string())),
      householdID: string(),
    }),
    withBeneficiaries: optional(nullable(array(beneficiarySchema))),
    withTrustees: optional(nullable(array(trusteeSchema))),
    withSuccessorTrustees: optional(nullable(array(trusteeSchema))),
    withTrustAdvisors: optional(nullable(array(trustAdvisorSchema))),
    withGrantors: optional(nullable(array(grantorSchema))),
    withDesignerAccount: optional(nullable(createAccountSchema)),
  });

const updateIrrevocableTrustSchema: Describe<AugmentedUpdateIrrevocableTrustInput> =
  object({
    id: string(),
    update: object({
      description: optional(nullable(string())),
      displayName: optional(nullable(string())),
      effectiveDate: optional(nullable(date())),
      gstStatus: gstStatusSchema,
      inEstateStatus: inEstateStatusSchema,
      stateInEstateStatus: inEstateStatusSchema,
      inclusionRatio: inclusionRatioSchema,
      legalName: optional(nullable(string())),
      taxStatus: taxStatusSchema,
      trustJurisdictionStateCode: optional(nullable(string())),
      clearEffectiveDate: optional(nullable(booleanType())),
      clearLegalName: optional(nullable(booleanType())),
      clearTrustJurisdictionStateCode: optional(nullable(booleanType())),
      clearBeneficiaries: optional(nullable(booleanType())),
      clearGrantors: optional(nullable(booleanType())),
      clearSuccessorTrustees: optional(nullable(booleanType())),
      clearTrustAdvisors: optional(nullable(booleanType())),
      clearDescription: optional(nullable(booleanType())),
      clearTrustees: optional(nullable(booleanType())),
      clearGstStatus: optional(nullable(booleanType())),
      clearTaxStatus: optional(nullable(booleanType())),
      clearInclusionRatio: optional(nullable(booleanType())),
      removePolicyIDs: optional(nullable(array(string()))),
    }),
    withBeneficiaries: optional(nullable(array(beneficiarySchema))),
    withTrustees: optional(nullable(array(trusteeSchema))),
    withSuccessorTrustees: optional(nullable(array(trusteeSchema))),
    withTrustAdvisors: optional(nullable(array(trustAdvisorSchema))),
    withGrantors: optional(nullable(array(grantorSchema))),
    updateDesignerAccount: optional(nullable(updateAccountSchema)),
    withPolicies: optional(nullable(array(createInsurancePolicyInput))),
    updatePolicies: optional(nullable(array(updateInsurancePolicyInput))),
  });

export const createSchema: Describe<AugmentedCreateEntityInput> = object({
  create: createEntityInput,
  withIrrevocableTrust: optional(nullable(createIrrevocableTrustSchema)),
});

export const updateSchema: Describe<AugmentedUpdateEntityInput> = object({
  id: string(),
  update: object(commonUpdateEntityProperties),
  updateIrrevocableTrust: optional(nullable(updateIrrevocableTrustSchema)),
});

export function validateIrrevocableTrustCreateInput(
  input: AugmentedCreateEntityInput
): AugmentedCreateEntityInput {
  return $getValidatedInput(input, createSchema, 'irrevocable trust create');
}

export function validateIrrevocableTrustUpdateInput(
  input: AugmentedUpdateEntityInput
): AugmentedUpdateEntityInput {
  return $getValidatedInput(input, updateSchema, 'irrevocable trust update');
}
